import { cssThemedValue } from '@creditornot/cb-ingredients';
import { DateTime } from 'luxon';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import {
  OpeningTimes as OpeningTimesT,
  VenuePatchData,
  ExceptionalOpeningTimes,
} from 'apis/restaurant-api/merchant-admin/venue/types';

import OpeningTimesForm from './OpeningTimesForm';
import OpeningTimesView from './OpeningTimesView';
import { reportOpeningTimesEdit, reportOpeningTimesEditCancel } from '../behavioral';

type Props = {
  className?: string;
  editable: boolean;
  effectiveOpeningTimes: OpeningTimesT | null;
  exceptionalOpeningTimes: ExceptionalOpeningTimes[];
  onSubmit: (data: VenuePatchData) => void;
  openingTimes: OpeningTimesT | null;
  submitting: boolean;
  submitSucceeded: boolean;
  timezone: string;
};

const Root = styled.div`
  position: relative;
  margin-block-end: 32px;
  padding-block-end: 24px;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
`;

const OpeningTimes = ({
  className,
  editable,
  effectiveOpeningTimes,
  exceptionalOpeningTimes,
  onSubmit,
  openingTimes,
  submitting,
  submitSucceeded,
  timezone,
}: Props) => {
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (submitSucceeded) {
      setEditing(false);
    }
  }, [submitSucceeded]);

  const hasSpecialHours = !!exceptionalOpeningTimes.filter(
    exception =>
      DateTime.fromMillis(exception.period.end.$date).setZone(timezone) >= DateTime.local().setZone(timezone),
  ).length;

  const handleEditToggle = () => {
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    reportOpeningTimesEditCancel();
  };

  const handleSubmit = (data: VenuePatchData) => {
    onSubmit(data);
    reportOpeningTimesEdit(hasSpecialHours);
  };

  return (
    <Root className={className}>
      {editing ? (
        <OpeningTimesForm
          submitting={submitting}
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          openingTimes={openingTimes}
          showSpecialHoursWarning={hasSpecialHours}
        />
      ) : (
        <OpeningTimesView
          effectiveOpeningTimes={effectiveOpeningTimes}
          onEdit={editable ? handleEditToggle : undefined}
          times={openingTimes}
          timezone={timezone}
        />
      )}
    </Root>
  );
};

export default OpeningTimes;
